/**
 * This module provides an interface to various browser-related services.
 */
import * as K from 'common/constants';
import { LocationOrURL, removeParamsFromUrl } from 'common/util/location';
import { AvailableTheme } from 'common/types';

// browser detection
const agent = navigator.userAgent;
export const isChrome = agent.indexOf('Chrome') !== -1;
export const isSafari = agent.indexOf('Safari') !== -1 && !isChrome;
export const isFirefox = agent.indexOf('Firefox') !== -1;

// Theming
const USER_PREFERS_DARK = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
export const DEFAULT_THEME = USER_PREFERS_DARK ? AvailableTheme.SENTINEL_ONE_DARK : AvailableTheme.SENTINEL_ONE;

/**
 * Fall back to
 * the deprecated execCommand().
 *
 * @param text            text to copy to clipboard
 * @param successCallback callback which is called in case of success
 * @param errorCallback   callback which is called in case of error
 *
 */
const copyToClipboardLegacyFallback = (
  text: string,
  successCallback?: () => void,
  errorCallback?: () => void,
): void => {
  const tempElement = document.createElement('textarea');
  tempElement.value = text;
  tempElement.className = 'offscreen';
  document.body.appendChild(tempElement);
  tempElement.focus();
  tempElement.select();
  let result;
  try {
    result = document.execCommand('copy');
  } catch (ignore) {
    result = null;
  } finally {
    document.body.removeChild(tempElement);
  }
  if (!result) {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const copyHotkey = isMac ? '⌘C' : 'CTRL+C';
    // eslint-disable-next-line no-alert
    result = prompt(`Press ${copyHotkey}`, text);
    if (!result) {
      if (errorCallback) {
        errorCallback();
      }
      return;
    }
    if (successCallback) {
      successCallback();
    }
  }
};

/**
 * Copy text to the clipboard using writeText() if available, otherwise fall back to
 * the deprecated execCommand().
 *
 * @param text            text to copy to clipboard
 * @param successCallback callback which is called in case of success
 * @param errorCallback   callback which is called in case of error
 *
 *
 */
export const copyToClipboard = (text: string, successCallback?: () => void, errorCallback?: () => void): void => {
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard
      .writeText(text)
      .then(successCallback)
      .catch(() => {
        copyToClipboardLegacyFallback(text, successCallback, errorCallback);
      });
  } else {
    copyToClipboardLegacyFallback(text, successCallback, errorCallback);
  }
};

export type DownloadConfig = {
  content: string;
  fileName: string;
  contentType?: string;
};

/**
 * This method will cause the browser to prompt users to download the provided content.
 *
 * @param content        content to be downloaded, as a properly formatted string
 * @param fileName       default file name that the browser will suggest
 * @param contentType    MIME type for the content, which will inform the file format that the browser will suggest.
 */
export const downloadFile = ({ content, fileName, contentType }: DownloadConfig): void => {
  const contentBlob = new Blob([content], { type: contentType });
  const tempAnchor = document.createElement('a');
  tempAnchor.style.display = 'none';
  document.body.appendChild(tempAnchor);

  // create a DOMString
  const contentDomString = window.URL.createObjectURL(contentBlob);
  tempAnchor.href = contentDomString;
  tempAnchor.download = fileName;
  tempAnchor.click();

  // cleanup
  window.URL.revokeObjectURL(contentDomString);
  document.body.removeChild(tempAnchor);
};

/**
 * This method will cause the browser to reload page without provided search params
 *
 * @param location          current page location
 * @param searchParams      list with search params to remove
 */
export const reloadPageWithoutSearchParams = (location: LocationOrURL, searchParams: Array<K.URL_PARAM>): void => {
  setTimeout(() => {
    // remove teamToken to avoid page error
    // token will be added on Angular automatically
    window.location.href = removeParamsFromUrl(location, searchParams);
  }, 250);
};

/**
 * Sets the theme for the UI. Theme is managed as a class on the <html> element. When it changes, we add a class to
 * have it fade in.
 *
 * @param theme    new theme to use
 */
export const setUITheme = (theme: AvailableTheme): void => {
  const htmlEl = document.documentElement;
  htmlEl.className = '';
  htmlEl.classList.add('theme-' + theme);
};

/**
 * Get the Range object for the current selection.
 */
export const getSelectedRange = (): Range | null => {
  if (window.getSelection) {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      return selection.getRangeAt(0);
    }
  }
  return null;
};

/**
 * Get the currently selected text.
 */
export const getSelectedText = (): string => {
  const range = getSelectedRange();
  return range ? range.toString() : '';
};

/**
 * Return the current cursor position within the given element.
 *
 * @param {Element} element    an element
 *
 * @return {number} cursor position, or -1 if it cannot be determined
 */
export const getCursorPosition = (element: HTMLTextAreaElement | HTMLInputElement | HTMLDivElement): number => {
  let cursorPos = 0;
  if (element instanceof HTMLDivElement) {
    const range = getSelectedRange();
    if (range) {
      const selLength = range.toString().length;
      const preRange = range.cloneRange();
      preRange.selectNodeContents(element);
      preRange.setEnd(range.endContainer, range.endOffset);
      cursorPos = preRange.toString().length - selLength;
    } else {
      return -1;
    }
  } else {
    const pos = (element as HTMLInputElement | HTMLTextAreaElement).selectionStart;
    cursorPos = pos == null ? -1 : pos;
  }

  return cursorPos;
};

/**
 * Return all the text nodes within the given element.
 *
 * @param {Element} element    an element
 *
 * @returns {Array} list of text nodes
 */
const getAllTextNodes = (element: HTMLDivElement): Array<Text> => {
  const a: Array<Text> = [];
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false);
  let n: Text = walker.nextNode() as Text;
  while (n) {
    a.push(n);
    n = walker.nextNode() as Text;
  }
  return a;
};

/**
 * Set the cursor to the given position within the given element.
 *
 * @param {Element} element     an element (INPUT, TEXTAREA, or DIV)
 * @param {number}  position    offset within element, or -1 for end
 */
export const setCursorPosition = (
  element: HTMLTextAreaElement | HTMLInputElement | HTMLDivElement,
  position: number,
): void => {
  let pos = position;

  if (element instanceof HTMLTextAreaElement || element instanceof HTMLInputElement) {
    if (pos === -1) {
      pos = element.value?.length || 1000;
    }
    element.focus();
    element.setSelectionRange(pos, pos);
    return;
  }

  const selection = window.getSelection();
  if (!selection) {
    return;
  }
  const range = document.createRange();
  const nodes = getAllTextNodes(element);
  if (nodes.length === 0) {
    return;
  }
  let node: Text | undefined;
  if (pos === -1) {
    node = nodes[nodes.length - 1];
    pos = node.length;
  } else {
    for (let n = 0; n < nodes.length; n += 1) {
      const value = nodes[n] && nodes[n].nodeValue;
      if (value !== null) {
        if (pos > value.length && nodes[n + 1]) {
          pos -= value.length;
        } else {
          node = nodes[n];
          break;
        }
      }
    }
  }

  if (node) {
    range.setStart(node, Math.min(pos, node.length));
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }
};

/**
 * Selects the text inside the given element.
 *
 * @param element    element
 */
export const selectElementContents = (element: Element): void => {
  if (window.getSelection && document.createRange) {
    const selection = window.getSelection();
    const range = document.createRange();
    if (selection && range) {
      range.selectNodeContents(element);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }
};

/**
 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
 *
 * @param {String} text The text to be measured.
 * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
 *
 * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 */
export function getTextWidth(text: string, font: string): number {
  // @ts-expect-error the "getTextWidth.canvas doesn't exist"
  const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement('canvas')); // re-use canvas object for better performance
  const context = canvas.getContext('2d');
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
}

/**
 * Redirect to url
 * @param url - full url to redirect to
 * @param replaceCurrent - if true redirect in same page, otherwise open in new tab
 */
export function redirectToUrl(url: string, replaceCurrent?: boolean) {
  window.open(url, replaceCurrent ? '_self' : '_blank', 'noopener,noreferrer');
}
